import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useGetExamsForDietQuery } from "../../api/examsApi";
import EligibilityBadge from "../../components/EligibilityBadge";
import ApplyExamModal from "../../components/ApplyExamModal";
import { stageLabel } from "../../utils/stage";

export default function ExamDietExamsPage() {
  const { dietId = "" } = useParams();
  const { data, isLoading, error, refetch } = useGetExamsForDietQuery(dietId, {
    skip: !dietId,
  });

  const [apply, setApply] = useState<{ examId: string; title: string } | null>(
    null
  );
  const [q, setQ] = useState("");

  const exams = data?.exams || [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return exams;
    return exams.filter(
      (e) =>
        e.title.toLowerCase().includes(s) || e.code.toLowerCase().includes(s)
    );
  }, [exams, q]);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link
            to="/exams/diets"
            className="text-sm text-gray-600 hover:underline"
          >
            ← Back to diets
          </Link>
          <h1 className="text-xl font-semibold text-gray-900 mt-2">
            {data?.diet?.title || "Diet"}
          </h1>
          <div className="text-sm text-gray-600 mt-1">
            Your Logbook Stage:{" "}
            <span className="font-medium text-gray-900">
              {stageLabel(data?.user_logbook_stage_passed ?? 0)}
            </span>
          </div>
        </div>

        <Link
          to="/exams/applications"
          className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm"
        >
          My Applications
        </Link>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search exams by title or code..."
          className="w-full md:w-96 border rounded-lg px-3 py-2 text-sm"
        />
        <button
          onClick={() => refetch()}
          className="px-4 py-2 rounded-lg bg-gray-900 text-white hover:bg-gray-800 text-sm"
        >
          Refresh
        </button>
      </div>

      <div className="mt-5">
        {isLoading ? (
          <div className="text-sm text-gray-600">Loading exams...</div>
        ) : error ? (
          <div className="text-sm text-rose-700">
            Failed to load exams.
            <button className="ml-2 underline" onClick={() => refetch()}>
              retry
            </button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-4">
            {filtered.map((e) => {
              const canApply = e.eligible && e.centers_count > 0;
              const reason = !e.eligible
                ? e.ineligible_reason
                : e.centers_count <= 0
                ? "No centers enabled for this exam in this diet yet."
                : null;

              return (
                <div key={e.id} className="border rounded-2xl p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold text-gray-900">
                        {e.title}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        {e.code} • {e.exam_type.toUpperCase()}
                      </div>
                    </div>
                    <EligibilityBadge eligible={canApply} reason={reason} />
                  </div>

                  <div className="mt-3 text-sm text-gray-700">
                    Centers available:{" "}
                    <span className="font-medium">{e.centers_count}</span>
                  </div>

                  {reason ? (
                    <div className="mt-2 text-xs text-rose-700">{reason}</div>
                  ) : null}

                  <div className="mt-4 flex items-center gap-2">
                    <button
                      disabled={!canApply}
                      onClick={() => setApply({ examId: e.id, title: e.title })}
                      className={[
                        "px-4 py-2 rounded-lg text-sm text-white",
                        canApply
                          ? "bg-gray-900 hover:bg-gray-800"
                          : "bg-gray-300",
                      ].join(" ")}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ApplyExamModal
        open={!!apply}
        onClose={() => setApply(null)}
        dietId={dietId}
        examId={apply?.examId || ""}
        examTitle={apply?.title || ""}
      />
    </div>
  );
}
